export { shuffleArray } from './shuffleArray';

// ========================================
// SHARED UTILITY FUNCTIONS
// ========================================

/**
 * Extracts the composite game job ID from the current URL.
 * Supports multiple URL patterns:
 * - URL parameters: gameId or compositeGameJobId
 * - Path patterns: /games/game/08-27-25/gjb_2130898747750614072/...
 * - Path patterns: /games/08-27-25/gjb_2130898747750614072/...
 * - Hash patterns: #/game/08-27-25/gjb_2130898747750614072/...
 *
 * @returns The composite game job ID if found, null otherwise
 */
export function extractCompositeGameJobId(): string | null {
  if (typeof window === "undefined") return null;
  const { href, search, hash } = window.location;

  // Check URL parameters first
  const urlParams = new URLSearchParams(search);
  const gameIdParam =
    urlParams.get("gameId") || urlParams.get("compositeGameJobId");
  if (gameIdParam) return gameIdParam;

  // Match pattern: /games/game/08-27-25/gjb_2130898747750614072/...
  const gamePathMatch = href.match(
    /\/games\/game\/([0-9]{2}-[0-9]{2}-[0-9]{2}\/gjb_[0-9]+)/
  );
  if (gamePathMatch) return gamePathMatch[1];

  // Match pattern: /games/08-27-25/gjb_2130898747750614072/...
  const pathMatch = href.match(
    /\/games\/([0-9]{2}-[0-9]{2}-[0-9]{2}\/gjb_[0-9]+)/
  );
  if (pathMatch) return pathMatch[1];

  // Match pattern: #/game/08-27-25/gjb_2130898747750614072/...
  const hashMatch = hash.match(
    /#\/game\/([0-9]{2}-[0-9]{2}-[0-9]{2}\/gjb_[0-9]+)/
  );
  if (hashMatch) return hashMatch[1];

  return null;
}

/**
 * Extracts voiceId and token from URL parameters.
 * Centralized utility to avoid duplicating URLSearchParams usage across components.
 *
 * @returns Object containing voiceId and token (both can be null if not found)
 */
export function extractVoiceIdAndToken(): {
  voiceId: string | null;
  token: string | null;
} {
  if (typeof window === "undefined") {
    return { voiceId: null, token: null };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    voiceId: params.get("voiceId"),
    token: params.get("token"),
  };
}

export const getAudioStorageKey = (id: string | null) =>
  id ? `kinzy_audio_urls_${id}` : null;

export const getAudioCacheKey = (text: string) => text.trim().toLowerCase();

export const getMuteStorageKey = (id: string | null) =>
  id ? `kinzy_audio_muted_${id}` : null;

// Utility function return types
export type VoiceIdAndToken = {
  voiceId: string | null;
  token: string | null;
};

export type UrlParameters = {
  voiceId: string | null;
  token: string | null;
  set: string | null;
  level: string | null;
  question: string | null;
  gameId: string | null;
  compositeGameJobId: string | null;
};